import React from 'react';
import { Box, Button, Typography } from '@mui/material';

interface AppErrorBoundaryProps {
  resetKey: string;
  children: React.ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export default class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(previous: AppErrorBoundaryProps): void {
    const { resetKey } = this.props;
    const { hasError } = this.state;
    if (hasError && previous.resetKey !== resetKey) {
      this.retry();
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('A page failed to render', error, info.componentStack);
  }

  retry = (): void => {
    this.setState({ hasError: false });
  };

  render(): React.ReactNode {
    const { hasError } = this.state;
    const { children } = this.props;
    if (!hasError) return children;
    return (
      <Box
        role="alert"
        sx={{
          maxWidth: 420,
          mx: 'auto',
          my: 8,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Something went wrong on this page.
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Your account is fine. Try the page again, or reload it.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button variant="outlined" onClick={this.retry}>
            Try again
          </Button>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </Box>
      </Box>
    );
  }
}
